<template>
  <b-nav-item-dropdown
    ref="notificationDropdown"
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
  >
    <template #button-content>
      <feather-icon
        :badge="totalCount || null"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          Notifications
        </h4>
        <b-badge pill variant="light-primary">
          {{ totalCount }} New
        </b-badge>
      </div>
    </li>

    <!-- Notification list -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <!-- Empty state -->
      <div v-if="items.length === 0" class="text-center text-muted py-2">
        <feather-icon icon="CheckCircleIcon" size="24" class="mb-50" />
        <div class="small">All caught up!</div>
      </div>

      <b-link
        v-for="item in items"
        :key="item.key"
        :to="item.route"
        @click="closeDropdown"
      >
        <b-media>
          <template #aside>
            <b-avatar size="32" :variant="item.variant">
              <feather-icon :icon="item.icon" size="16" />
            </b-avatar>
          </template>
          <p class="media-heading mb-0">
            <span class="font-weight-bolder">{{ item.title }}</span>
          </p>
          <small class="notification-text">{{ item.subtitle }}</small>
        </b-media>
      </b-link>
    </vue-perfect-scrollbar>

    <!-- Footer -->
    <li class="dropdown-menu-footer">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
        :to="{ name: 'dashboard-fbr' }"
        @click="closeDropdown"
      >
        View Dashboard
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Ripple from 'vue-ripple-directive'
import { computed, ref } from '@vue/composition-api'
import moment from 'moment'
import store from '@/store'

export default {
  components: {
    BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton, VuePerfectScrollbar,
  },
  directives: { Ripple },

  setup() {
    const notificationDropdown = ref(null)
    const closeDropdown = () => {
      notificationDropdown.value?.hide(true)
    }

    const chatUnread = computed(() => store.getters['chatbot/unreadCount'])
    const pendingProperty = computed(() => store.getters['notifications/pendingPropertyCount'])
    const expiringProperty = computed(() => store.getters['notifications/expiringPropertyCount'])
    const leaseExpiring = computed(() => store.getters['notifications/leaseExpiringCount'])
    const ownerUpdateProperty = computed(() => store.getters['notifications/ownerUpdatePropertyCount'])

    const items = computed(() => {
      const list = []

      if (chatUnread.value > 0) {
        list.push({
          key: 'chat',
          title: 'New Chat Messages',
          subtitle: `${chatUnread.value} unread message${chatUnread.value > 1 ? 's' : ''} from customers`,
          icon: 'MessageSquareIcon',
          variant: 'light-info',
          route: { name: 'chat-bot' },
        })
      }

      if (pendingProperty.value > 0) {
        list.push({
          key: 'property',
          title: 'Property Requests',
          subtitle: `${pendingProperty.value} propert${pendingProperty.value > 1 ? 'ies' : 'y'} pending approval`,
          icon: 'HomeIcon',
          variant: 'light-warning',
          route: { name: 'property-request-lists' },
        })
      }

      if (expiringProperty.value > 0) {
        list.push({
          key: 'expiring-property',
          title: 'Property Expiring Soon',
          subtitle: `${expiringProperty.value} listing${expiringProperty.value > 1 ? 's' : ''} expiring within 2 months`,
          icon: 'ClockIcon',
          variant: 'light-danger',
          route: {
            name: 'property-lists',
            query: {
              expire_start: moment().startOf('day').format('YYYY-MM-DD'),
              expire_end: moment().add(2, 'months').endOf('day').format('YYYY-MM-DD'),
            },
          },
        })
      }

      if (ownerUpdateProperty.value > 0) {
        list.push({
          key: 'owner-update-property',
          title: 'Owner Updated Property',
          subtitle: `${ownerUpdateProperty.value} update${ownerUpdateProperty.value > 1 ? 's' : ''} awaiting approval`,
          icon: 'EditIcon',
          variant: 'light-secondary',
          route: { name: 'dashboard-fbr', query: { tab: 'owner-updates' } },
        })
      }

      if (leaseExpiring.value > 0) {
        list.push({
          key: 'lease-expiring',
          title: 'Lease Expiring Soon',
          subtitle: `${leaseExpiring.value} tenant contract${leaseExpiring.value > 1 ? 's' : ''} need renewal within 2 months`,
          icon: 'FileTextIcon',
          variant: 'light-primary',
          route: {
            name: 'close-deal-lists',
            query: {
              action: 'Rent',
              expireStartDate: moment().startOf('day').format('YYYY-MM-DD'),
              expireEndDate: moment().add(2, 'months').endOf('day').format('YYYY-MM-DD'),
            },
          },
        })
      }

      return list
    })

    const totalCount = computed(() => chatUnread.value + pendingProperty.value + expiringProperty.value
      + leaseExpiring.value + ownerUpdateProperty.value)

    const perfectScrollbarSettings = { maxScrollbarLength: 60, wheelPropagation: false }

    return {
      notificationDropdown, closeDropdown, items, totalCount, perfectScrollbarSettings,
    }
  },
}
</script>
